import findKey from 'lodash/findKey';

/**
 * Used to enforce that switch statements are exhaustive.
 *
 * See: https://stackoverflow.com/questions/39419170/how-do-i-check-that-a-switch-block-is-exhaustive-in-typescript
 *
 * @param {never} x
 *
 * @example
 * function getColorName(c: 'red' | 'green'): string {
 *   switch (c) {
 *       case 'red':
 *           return 'It is red';
 *       case 'green':
 *           return 'It is green';
 *   }
 *   return assertUnreachable(c);
 * }
 */
export function assertUnreachable(_x: never): never {
  throw new Error(`Reached code that should be unreachable with value: ${_x}`);
}

export function findKeyByValue<K extends string>(obj: {[key in K]?: string}, value: string): K {
  return findKey(obj, (v) => v === value) as K;
}

/**
 * Extract the type of Value from Array<Value>.
 *
 * @example
 * ArrayType<number[]>;    // number
 * ArrayType<Array<A | B>> // A | B
 * ArrayType<string>;      // never
 */
export type ArrayType<T> = T extends Array<infer U> ? U : never;

/**
 * Extract keys from Union Type
 *
 * @example
 * KeysOfUnion<{one:any, two:any} | {three:any, four:any}> // "one"|"two"|"three"|"four"
 */
export type KeysOfUnion<T> = T extends any ? keyof T : never;

export type DeepPartial<T> = T extends never[]
  ? T
  : {
      [P in keyof T]?: DeepPartial<T[P]>;
    };
